import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiDownload } from 'react-icons/fi';
import api from '../../services/api';
import DataTable from '../../components/common/DataTable';

const SHIPMENT_TYPE_MAP = {
  truck: { prefix: '/truck', label: 'Truck', color: '#2563eb' },
  ocean_import: { prefix: '/ocean-import', label: 'OI', color: '#0891b2' },
  ocean_export: { prefix: '/ocean-export', label: 'OE', color: '#7c3aed' },
  air_import: { prefix: '/air-import', label: 'AI', color: '#ea580c' },
  misc: { prefix: '/misc', label: 'Misc', color: '#64748b' },
};

const ProfitReport = () => {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [summary, setSummary] = useState({
    total_revenue: 0, total_cost: 0, total_profit: 0,
    overall_margin: 0, shipment_count: 0, profitable_count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/profit-report');
      setShipments(res.data.shipments || []);
      setSummary(res.data.summary || {});
    } catch (error) {
      toast.error('Failed to load profit report');
    } finally {
      setLoading(false);
    }
  };

  const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Filter
  let filtered = shipments;
  if (typeFilter) filtered = filtered.filter(s => s.shipment_type === typeFilter);
  if (monthFilter) {
    const mon = parseInt(monthFilter);
    filtered = filtered.filter(s => {
      if (!s.post_date) return false;
      return new Date(s.post_date).getMonth() + 1 === mon;
    });
  }
  if (searchQuery) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter(s =>
      (s.file_no || '').toLowerCase().includes(q) ||
      (s.customer || '').toLowerCase().includes(q) ||
      (s.mbl_no || '').toLowerCase().includes(q) ||
      (s.op || '').toLowerCase().includes(q)
    );
  }

  // Recalculate filtered summary
  const filteredRevenue = filtered.reduce((sum, s) => sum + s.total_revenue, 0);
  const filteredCost = filtered.reduce((sum, s) => sum + s.total_cost, 0);
  const filteredProfit = filteredRevenue - filteredCost;
  const filteredMargin = filteredRevenue > 0 ? ((filteredProfit / filteredRevenue) * 100) : 0;

  const formatCurrency = (val) => {
    const num = Number(val) || 0;
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleRowClick = (row) => {
    const typeInfo = SHIPMENT_TYPE_MAP[row.shipment_type] || SHIPMENT_TYPE_MAP.truck;
    navigate(`${typeInfo.prefix}/${row.id}`);
  };

  const [exporting, setExporting] = useState(false);

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const params = {};
      if (typeFilter) params.type = typeFilter;
      if (monthFilter) params.month = monthFilter;
      if (searchQuery) params.search = searchQuery;

      const res = await api.get('/profit-report/export', {
        params,
        responseType: 'blob',
      });

      // Build filename
      let filename = 'Profit_Report';
      if (monthFilter) filename += `_${MONTH_NAMES[parseInt(monthFilter) - 1]}`;
      if (typeFilter) {
        const typeLabel = SHIPMENT_TYPE_MAP[typeFilter]?.label || typeFilter;
        filename += `_${typeLabel}`;
      }
      filename += '.xlsx';

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      key: 'file_no', label: 'File No.', sortable: true,
      render: (v, row) => {
        const typeInfo = SHIPMENT_TYPE_MAP[row.shipment_type] || SHIPMENT_TYPE_MAP.truck;
        return (
          <span
            style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 500 }}
            onClick={(e) => { e.stopPropagation(); navigate(`${typeInfo.prefix}/${row.id}`); }}
          >
            {v || '-'}
          </span>
        );
      },
    },
    {
      key: 'shipment_type', label: 'Type', sortable: true,
      render: (v) => {
        const info = SHIPMENT_TYPE_MAP[v] || { label: v, color: '#64748b' };
        return (
          <span style={{
            background: info.color + '18', color: info.color, fontWeight: 600,
            fontSize: '10px', padding: '2px 8px', borderRadius: '10px',
          }}>
            {info.label}
          </span>
        );
      },
    },
    {
      key: 'post_date', label: 'Post Date', sortable: true,
      render: (v) => v ? new Date(v).toLocaleDateString() : '-',
    },
    { key: 'customer', label: 'Customer', sortable: true },
    { key: 'mbl_no', label: 'MB/L No.', sortable: true },
    { key: 'op', label: 'OP', sortable: true },
    {
      key: 'total_revenue', label: 'Revenue', sortable: true,
      render: (v) => (
        <span style={{ display: 'block', textAlign: 'right', color: '#059669', fontWeight: 500 }}>
          {formatCurrency(v)}
        </span>
      ),
    },
    {
      key: 'total_cost', label: 'Cost', sortable: true,
      render: (v) => (
        <span style={{ display: 'block', textAlign: 'right', color: '#dc2626', fontWeight: 500 }}>
          {formatCurrency(v)}
        </span>
      ),
    },
    {
      key: 'profit', label: 'Profit', sortable: true,
      render: (v) => {
        const num = Number(v) || 0;
        const color = num >= 0 ? '#059669' : '#dc2626';
        return (
          <span style={{ display: 'block', textAlign: 'right', color, fontWeight: 700 }}>
            {formatCurrency(v)}
          </span>
        );
      },
    },
    {
      key: 'margin', label: 'Margin %', sortable: true,
      render: (v) => {
        const num = Number(v) || 0;
        const color = num >= 0 ? '#059669' : '#dc2626';
        return (
          <span style={{ display: 'block', textAlign: 'right', color, fontWeight: 600 }}>
            {num.toFixed(1)}%
          </span>
        );
      },
    },
    { key: 'charge_count', label: 'Charges', sortable: true },
    { key: 'status', label: 'Status', sortable: true, type: 'status' },
  ];

  return (
    <div>
      <div className="card">
        {/* Header */}
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="card-title" style={{ margin: 0 }}>PROFIT REPORT</h2>
          <button
            className="btn btn-primary"
            onClick={handleExportExcel}
            disabled={exporting || filtered.length === 0}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', padding: '7px 16px' }}
          >
            <FiDownload size={14} />
            {exporting ? 'Exporting...' : 'Export Excel'}
          </button>
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', padding: '16px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '14px' }}>
            <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Total Revenue</div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: '#059669' }}>${formatCurrency(filteredRevenue)}</div>
          </div>
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '14px' }}>
            <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Total Cost</div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: '#dc2626' }}>${formatCurrency(filteredCost)}</div>
          </div>
          <div style={{ background: filteredProfit >= 0 ? '#f0fdf4' : '#fef2f2', border: `1px solid ${filteredProfit >= 0 ? '#bbf7d0' : '#fecaca'}`, borderRadius: '8px', padding: '14px' }}>
            <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Net Profit</div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: filteredProfit >= 0 ? '#059669' : '#dc2626' }}>${formatCurrency(filteredProfit)}</div>
          </div>
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '14px' }}>
            <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Profit Margin</div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: '#2563eb' }}>{filteredMargin.toFixed(1)}%</div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
              {filtered.length} shipments
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filter-bar">
          <div className="filter-group">
            <label className="filter-label">Module:</label>
            <select className="filter-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">All</option>
              <option value="truck">Truck</option>
              <option value="ocean_import">Ocean Import</option>
              <option value="ocean_export">Ocean Export</option>
              <option value="air_import">Air Import</option>
              <option value="misc">Misc</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Month:</label>
            <select className="filter-select" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
              <option value="">All</option>
              {MONTH_NAMES.map((name, i) => (
                <option key={i + 1} value={i + 1}>{name}</option>
              ))}
            </select>
          </div>
          <div className="filter-group" style={{ flex: 1, marginLeft: 'auto' }}>
            <input
              type="text"
              className="filter-input"
              placeholder="Search by file no, customer, MB/L, OP..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <DataTable
          columns={columns}
          data={filtered}
          loading={loading}
          onRowClick={handleRowClick}
          pageSize={20}
        />
      </div>
    </div>
  );
};

export default ProfitReport;
